import { makeAutoObservable } from "mobx";
import { IOrder, IOrderedItem } from "@drone-parts.garphild.pro/interfaces";
import dayjs from "dayjs";
import dronesStorage from "./drones.storage";
import * as RestApi from '../helpers/rest-api-query';
import { notification } from "antd";


export type IGroupedItems = Record<string, Record<string, Record<string, {
  qty: number,
  variants: Set<string>,
  orderedQty: number,
  cost: number,
  partTypes: string[]
}>>>;

export interface IGroupedItemsResult {
  items: IGroupedItems;
  total: number;
  totalEconomy: number;
}

interface ITrackData {
  trackNumber: string
  status: string
  orders: IOrder[]
  children: IOrder[]
  address: string
  recipient: string
  carrier: string
}

class OrdersStorage {
  orders: IOrder[] = [];
  mode: 'all' | 'uncategorized' = 'all';
  activeKey = 'all'
  private expandedRowKeysArray: Record<string, boolean> = {}
  private expandTrackingRowKeysArray: Record<string, boolean> = {}
  statusFilter = 'all';
  searchTerm = '';

  constructor() {
      makeAutoObservable(this);
  }

  get overallStats() {
    return [
      {
        name: "Total orders",
        value: this.orders.length,
      },
      {
        name: "Total items",
        value: this.orders.reduce((acc, order) => acc + order.orderItems.length, 0),
      },
      {
        name: "Total quantity",
        value: this.orders.reduce((acc, order) => {
          try {
            const totalQty = order.orderItems.reduce((acc, item) => acc + (item.itemQuantity || 0), 0);
            return acc + totalQty;
          } catch (e) {
            console.error(e);
            return acc;
          }
        }, 0),
      },
      {
        name: "Total spent",
        value: this
          .orders
          .filter((order) => {
            return (
              !["Expired", "Cancelled", "Canceled", "Planning"].includes(order.orderStatus)
              && !['to order', 'Self made'].includes(order.sellerId)
            )
          })
          .reduce((acc, order) => acc + order.orderTotal, 0),
        currency: "$",
      },
      ...this.statuses.map(status => {
        return {
          name: status,
          value: this.orders.filter(order => order.orderStatus === status).length,
        }
      }),
    ]
  }

  get stockStats() {
    return [
      {
        name: "Total items in stock",
        value: this.productsInStock.length,
      },
      {
        name: "Total qty in stock",
        value: this.productsInStock.reduce((acc, item) => acc + (item.used?.reduce((acc, usedItem) => acc - usedItem.quantity, (item.inStock || 0)) || 0), 0),
      },

      {
        name: "Total sent",
        value: dronesStorage.drones.reduce((acc, drone) => acc + (drone.status === 'sent' ? drone.parts.reduce((acc, part) => acc + (part.total || 0), 0 ) || 0 : 0), 0), currency: "$" },
      {
        name: "Total cost to delivery",
        value: this.orders.reduce((acc, order) => {
          if (order.orderItems.some((item) => item.received)) {
            return acc;
          }
          return acc + (order.orderTotal || 0);
        }, 0),
        currency: "$",
      },
    ]
  }

  get marketplaces() {
    const marketplaces = new Set(this.orders.map(order => order.marketplaceId));
    return [...marketplaces];
  }

  get sellers() {
    const sellers: Record<string, string[]> = {};
    this.orders.forEach(order => {
      if (!sellers[order.marketplaceId]) {
        sellers[order.marketplaceId] = [];
      }
      if (!sellers[order.marketplaceId].includes(order.sellerId)) {
        sellers[order.marketplaceId].push(order.sellerId);
      }
    });
    return sellers;
  }

  get statuses() {
    const statuses = new Set(this.orders.map(order => order.orderStatus));
    return [...statuses];
  }

  get currentOrders() {
    if (this.mode === 'all') {
      return this.orders;
    }

    return this
      .orders
      .filter((order) => {
        if (!order.category || order.orderItems?.some((item) => !item.partTypes || !item.partTypes?.length)) {
          if (this.searchTerm) {
            return order.orderItems.some((item) => item.itemText?.toLowerCase().includes(this.searchTerm.toLowerCase()));
          }
          return true;
        }
        return false;
      });
  }

  get expandedRowKeys() {
    return Object.keys(this.expandedRowKeysArray).filter(Boolean);
  }

  get shippingAddresses() {
    const addresses = new Set<string>();
    this.orders.forEach(order => {
      addresses.add(order.orderShippingAddress?.replace("\n", " "));
    });
    return [...addresses];
  }

  get items(): IOrderedItem[] {
    const items: (IOrderedItem & { orderStatus: string, marketplaceId: string })[] = [];
    this.orders.forEach(order => {
      items.push(...order.orderItems.map((item, index) => ({
        ...item,
        orderNumber: order.orderId,
        orderDate: order.orderDate,
        marketplaceId: order.marketplaceId,
        sellerId: order.sellerId,
        trackingNumbers: order.trackingNumbers,
        index,
        orderStatus: order.orderStatus,
        itemQuantity: item.itemQuantity || 0,
        itemPrice: item.itemPrice || 0,
        inStock: item.inStock || 0,
        used: item.used || [],
        unitsInPackage: item.unitsInPackage || 0,
        orderId: order.orderId,
      })));
    });
    return items;
  }

  get tracking(): ITrackData[] {
    const items: Record<string, ITrackData> = {};
    this.orders.forEach(order => {
      if (order.orderStatus !== 'Completed' && order.trackingNumbers?.length) {
        order.trackingNumbers.filter(Boolean).forEach((trackingNumber) => {
          let carrier = 'unknown';
          if (trackingNumber.startsWith('A')) {
            carrier = 'NP';
          } else if (trackingNumber.startsWith('M')) {
            carrier = 'Meest';
          } else if (trackingNumber.startsWith('R')) {
            carrier = 'Ukrposhta';
          }
          if (items[trackingNumber]) {
            items[trackingNumber].orders.push(order);
            items[trackingNumber].children.push(order);
          } else {
            items[trackingNumber] = ({
              trackNumber: trackingNumber,
              status: order.orderStatus,
              address: order.orderShippingAddress,
              recipient: order.orderShippingAddress.split(/\s/).slice(0,2).join(" "),
              carrier,
              orders: [order],
              children: [order],
            });
          }
        });
      }
    });
    return Object.values(items).sort((a, b) => {
      return a.trackNumber.localeCompare(b.trackNumber);
    });
  }

  get shortNames() {
    const names = new Set<string>();
    this.orders.forEach(order => {
      order.orderItems.forEach(item => {
        if (item.shortName) {
          names.add(item.shortName);
        }
      });
    });
    return [...names];
  }

  loadOrders = async () => {
    try {
      const orders = await RestApi.get<IOrder[]>(RestApi.ApiEndpoints.Orders);
      this.setOrders(orders);
      notification.success({ message: "Orders loaded" });
    } catch (error) {
      notification.error({ message: "Orders load failed" });
    }
  }

  setOrders = (orders: IOrder[] | null) => {
    this.orders = (orders || []).map((item: IOrder) => {
      return {
        ...item,
        id: `${item.orderId}-${item.marketplaceId}`,
        orderDate: item.orderDate ? dayjs(item.orderDate) : dayjs(null),
        estimatedDelivery: item.estimatedDelivery ? dayjs(item.estimatedDelivery) : dayjs(null),
        orderEconomy: +item.orderEconomy?.toFixed(2) || 0,
        trackingUrl: item.trackingUrl?.[0] === '/' ? `https:${item.trackingUrl}` : item.trackingUrl,
      };
    });
  }

  setCategory = async (orderId: string, marketplaceId: string, value: string) => {
    try {
      const orders = await RestApi
        .post<Partial<IOrder>[], IOrder[]>(
          RestApi.ApiEndpoints.Orders,
          [{ orderId, marketplaceId, category: value }],
        )
      if (!orders) {
        return;
      }
      this.updateCurrentOrders(orders)
      notification.success({ message: "Category updated" });
    } catch (error) {
      notification.error({ message: "Category update failed" });
    }
  }

  updateItem = async (orderId: string, marketplaceId: string, index: number, fieldName: keyof IOrderedItem, value: any) => {
    try {
      const orderedItems = this.orders.find(order => order.orderId === orderId && order.marketplaceId === marketplaceId)?.orderItems;
      if (!orderedItems) {
        return;
      }
      const item = orderedItems[index];
      // @ts-ignore
      item[fieldName] = value;
      const orders = await RestApi.post<Partial<IOrder>[], IOrder[]>(
        '/api/orders',
        [{
          orderId,
          marketplaceId,
          orderItems: [...orderedItems]
        }]
      )
      if (!orders) {
        return;
      }
      this.updateCurrentOrders(orders)
      notification.success({ message: "Item updated" });
    } catch (error) {
      notification.error({ message: "Item update failed" });
    }
  }

  markItemBroken = async (orderId: string, marketplaceId: string, index: number) => {
    try {
      const orderedItems = this.orders.find(order => order.orderId === orderId && order.marketplaceId === marketplaceId)?.orderItems;
      if (!orderedItems) {
        return;
      }
      const item = orderedItems[index];
      const usedBroken = item.used?.find(used => used.droneId === 'broken');
      if (usedBroken) {
        usedBroken.quantity += 1;
      } else {
        item.used?.push({ droneId: 'broken', quantity: 1 });
      }
      const orders = await RestApi.post<Partial<IOrder>[], IOrder[]>(
        '/api/orders',
        [{
          orderId,
          marketplaceId,
          orderItems: [...orderedItems]
        }]
      )
      if (!orders) {
        return;
      }
      this.updateCurrentOrders(orders)
      notification.success({ message: "Item marked as broken" });
    } catch (error) {
      notification.error({ message: "Item mark as broken failed" });
    }
  }

  private updateCurrentOrders = (orders: IOrder[]) => {
    const newOrders = [...this.orders];
    for (const order of orders) {
      const currentOrderIndex = this.orders.findIndex(currentOrder => order.orderId === currentOrder.orderId && order.marketplaceId === currentOrder.marketplaceId);
      if (currentOrderIndex === -1) {
        newOrders.unshift(order);
        continue;
      }
      newOrders[currentOrderIndex] = order;
    }
    this.setOrders(newOrders);
  }

  setItemCategory = async (orderId: any, marketplaceId: any, index: number, value: string) => {
    try {
      const orderedItems = this.orders.find(order => order.orderId === orderId && order.marketplaceId === marketplaceId)?.orderItems;
      if (!orderedItems?.[index]) {
        return;
      }
      orderedItems[index].category = value;
      const orders = await RestApi
        .post<Partial<IOrder>[], IOrder[]>(
        '/api/orders',
        [{
          orderId,
          marketplaceId,
          orderItems: [...orderedItems]
        }]
      )
      if (!orders) {
        return;
      }
      this.updateCurrentOrders(orders)
      notification.success({ message: "Item category updated" });
    } catch (error) {
      notification.error({ message: "Item category update failed" });
    }
  }

  setItemPart = async (orderId: any, marketplaceId: any, index: number, value: string) => {
    try {
      const orderedItems = this.orders.find(order => order.orderId === orderId && order.marketplaceId === marketplaceId)?.orderItems;
      if (!orderedItems?.[index]) {
        return;
      }
      orderedItems[index].part = value;
      const orders = await RestApi.post<Partial<IOrder>[], IOrder[]>(
        '/api/orders',
        [{
          orderId,
          marketplaceId,
          orderItems: [...orderedItems]
        }]
      );
      if (!orders) {
        return;
      }
      this.updateCurrentOrders(orders)
      notification.success({ message: "Item part updated" });
    } catch (error) {
      notification.error({ message: "Item part update failed" });
    }
  }

  setShortName = async (oldName: string, newName: string) => {
    try {
      const orderedItems = this
        .orders
        .filter((order) => order.orderItems.some((item) => item.itemText === oldName || item.shortName === oldName));
      if (!orderedItems) {
        return;
      }
      const updatedItems = orderedItems.map((item) => {
        return {
          orderId: item.orderId,
          marketplaceId: item.marketplaceId,
          orderItems: item.orderItems.map((item) => {
            if (item.itemText === oldName || item.shortName === oldName) {
              item.shortName = newName;
            }
            return item;
          })
        }
      });

     const orders = await RestApi
      .post<Partial<IOrder>[], IOrder[]>(
        '/api/orders',
        updatedItems
      );  
      if (!orders) {
        return;
      }
      this.updateCurrentOrders(orders)
      notification.success({ message: "Short name updated" });
    } catch (error) {
      notification.error({ message: "Short name update failed" });
    }
  }

  setActiveKey = (key: string) => {
    this.activeKey = key;
  }

  expandRow = (key: string, expanded: boolean) => {
    if (expanded) {
      this.expandedRowKeysArray = {
        ...this.expandedRowKeysArray,
        [key]: expanded,
      };
    } else {
      delete this.expandedRowKeysArray[key];
    }
    this.expandedRowKeysArray = { ...this.expandedRowKeysArray };
  }

  expandTrackingRow = (key: string, expanded: boolean) => {
    if (expanded) {
      this.expandTrackingRowKeysArray = {
        ...this.expandTrackingRowKeysArray,
        [key]: expanded,
      };
    } else {
      delete this.expandTrackingRowKeysArray[key];
    }
    this.expandTrackingRowKeysArray = { ...this.expandTrackingRowKeysArray };
  }

  clearExpandedRowKeys = () => {
    this.expandedRowKeysArray = {};
  }

  setStatusFilter = (status: string) => {
    this.statusFilter = status;
  }

  markReceived = async (order: IOrder, orderedItem: IOrderedItem) => {
    return this.updateItem(order.orderId, order.marketplaceId, order.orderItems.indexOf(orderedItem), 'received', true);
  }

  markBroken = async (order: IOrder, orderedItem: IOrderedItem) => {
    return this.markItemBroken(order.orderId, order.marketplaceId, order.orderItems.indexOf(orderedItem));
  }

  get itemsToOrder() {
    const items: IOrderedItem[] = [];
    this.orders.forEach(order => {
      if (order.marketplaceId === 'to order') {
        order.orderItems.forEach((item, index) => {
          items.push({
            ...item,
            orderNumber: order.orderId,
            orderDate: order.orderDate,
            index,
          });
        });
      }
    });
    return items;
  }

  get productsInStock() {
    const items: IOrderedItem[] = [];
    this.orders.forEach(order => {
      const orderPrice = order.orderItems.reduce((acc, item) => {
        return acc + (item.itemPrice || 0) * (+(item.itemQuantity || 0));
      }, 0);
      const orderChange = order.orderTotal / orderPrice;
      order.orderItems.forEach((item, index) => {
        // if (item.received) {
          items.push({
            ...item,
            orderNumber: order.orderId,
            orderDate: order.orderDate,
            index,
            itemPrice: (item.itemPrice || 0) * orderChange / (item?.unitsInPackage || 1),
          });
        // }
      });
    });
    return items;
  }

  get overallCategoriesInStock() {
    const categories = new Map<string, number>();
    this.productsInStock.forEach(item => {
      const category = item.part || 'Uncategorized';
      const itemsCount = item.used?.reduce((acc, usedItem) => acc - usedItem.quantity, (item.inStock || 0)) || 0;
      if (itemsCount > 0) {
        categories.set(category, (categories.get(category) || 0) + itemsCount);
      }
    });
    return categories;
  }

  get realItemsInStock() {
    const items: IOrderedItem[] = [];
    this.productsInStock.forEach(item => {
      const inStock = item.used?.reduce((acc, usedItem) => acc - usedItem.quantity, (item.inStock || 0)) || 0;
      if (inStock > 0) {
        items.push({
          ...item,
          inStock,
        });
      }
    });
    return items;
  }

  get groupedItems_v2(): IGroupedItemsResult {
    const items: IGroupedItems = {};
    let total = 0;
    let totalEconomy = 0;

    for (const order of this.orders) {
      if (order.marketplaceId === 'to order') continue;

      for (const item of order.orderItems) {
        const partTypes = item.partTypes || ['unknown:unknown'];
        const used = item.used?.reduce((acc, used) => acc + (used.quantity || 0), 0) || 0;
        item.inStock = (item.inStock || 0) - used;
        const name = item.shortName || item.itemText || "Unknown item";
        for (const partType of partTypes) {
          const [category, part] = partType.split(':');
          if (!items[category]) {
            items[category] = {};
          }
          if (!items[category][part]) {
            items[category][part] = {};
          }
          if (!items[category][part][name]) {
            items[category][part][name] = {
              qty: 0,
              variants: new Set<string>(),
              orderedQty: 0,
              cost: 0,
              partTypes: [],
            };
          }
          items[category][part][name].partTypes = [...new Set([...items[category][part][name].partTypes, partType])];
          items[category][part][name].qty += item.inStock;
          items[category][part][name].orderedQty += item.itemQuantity || 0;
          items[category][part][name].cost += (item.itemPrice || 0) * (item.itemQuantity || 0);
          if (item.itemVariant) {
            items[category][part][name].variants.add(item.itemVariant);
          }
        }
        total += (item.itemPrice || 0) * (item.itemQuantity || 0);
      }
      totalEconomy += parseFloat(order.orderEconomy.toString());
    }

    return { items, total, totalEconomy };
  }

  setItemField = (orderId: string, marketplaceId: string, _id: string | undefined, value: any, fieldName: keyof IOrderedItem) => {
    if (!_id) return;
    const orderIndex = this.orders.findIndex(order => order.orderId === orderId && order.marketplaceId === marketplaceId);
    const itemIndex = this.orders[orderIndex].orderItems.findIndex(item => item._id === _id);
    this.updateItem(orderId, marketplaceId, itemIndex, fieldName, value);
  }

  setOrderStatus = async (orderId: string, marketplaceId: string, _id: string, value: string) => {
    try {
      const orders = await RestApi.post<Partial<IOrder>[], IOrder[]>(
        '/api/orders',
        [{
          orderId,
          marketplaceId,
          orderStatus: value,
        }]
      );
      if (!orders) {
        throw new Error("Failed to update order status");
      }
      this.updateCurrentOrders(orders)
      notification.success({ message: "Order status updated" });
    } catch (error) {
      notification.error({ message: "Failed to update order status" });
    }
  }

  setSearch = (value: string) => {
    this.searchTerm = value;
  }

  clearSearch = () => {
    this.searchTerm = '';
  }

  createOrder = async (fieldValues: Partial<IOrder>) => {
    try {
      const orders = await RestApi.post<Partial<IOrder>[], IOrder[]>(
        '/api/orders',
        [fieldValues]
      );
      if (!orders) {
        throw new Error("Failed to create order");
      }
      this.updateCurrentOrders(orders)
      notification.success({ message: "Order created" });
    } catch (error) {
      notification.error({ message: "Failed to create order" });
    }
  }

  hideRow = async (orderId: string) => {
    try {
      const orders = await RestApi.post<Partial<IOrder>[], IOrder[]>(
        '/api/orders',
        [{
          _id: orderId,
          hidden: true,
        }]
      );    
      const currentOrderIndex = this.orders.findIndex(order => order._id === orderId);
      if (currentOrderIndex === -1) {
        return;
      }
      const newOrders = [...this.orders];
      newOrders.splice(currentOrderIndex, 1);
      this.setOrders(newOrders);
      notification.success({ message: "Order hidden" });
    } catch (error) {
      notification.error({ message: "Failed to hide order" });
    }
  }

  deleteRow = async (orderId: string) => {
    try {
      await RestApi.del(`/api/orders/${orderId}`);
      const currentOrderIndex = this.orders.findIndex(order => order._id === orderId);
      if (currentOrderIndex === -1) {
        return;
      }
      const newOrders = [...this.orders];
      newOrders.splice(currentOrderIndex, 1);
      this.setOrders(newOrders);
      notification.success({ message: "Order deleted" });
    } catch (error) {
      notification.error({ message: "Failed to delete order" });
    }
  }
}

const ordersStorage = new OrdersStorage();

export default ordersStorage;
